import { useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { api, ApiError, toApiError, unwrap } from "../../api/client";
import { ErrorMessage, Loading, SeverityBadge } from "../../components/ui";
import { t, type MessageKey } from "../../i18n/t";
import { formatDateTime, formatNumber, severityLabel } from "../../lib/format";
import { routes } from "../../lib/routes";
import { usePageTitle } from "../../lib/usePageTitle";
import { useQuery } from "../../lib/useQuery";
import { useKommune } from "./KommuneContext";

/** One entry of a change diff (`diff.new|resolved|changed[]`, untyped in the API). */
interface DiffItem {
  key: string;
  title: string;
  location: string;
  severity: string;
  rank: number | null;
  from: string | null;
  to: string | null;
}

function readItems(v: unknown): DiffItem[] {
  if (!Array.isArray(v)) return [];
  return v
    .filter((x): x is Record<string, unknown> => typeof x === "object" && x !== null)
    .map((x) => ({
      key: typeof x.key === "string" ? x.key : "",
      title: typeof x.title === "string" ? x.title : "",
      location: typeof x.location === "string" ? x.location : "",
      severity: typeof x.severity === "string" ? x.severity : "",
      rank: typeof x.rank === "number" ? x.rank : null,
      from: typeof x.from === "string" ? x.from : null,
      to: typeof x.to === "string" ? x.to : null,
    }));
}

type Group = "new" | "resolved" | "changed";
const GROUPS: { id: Group; title: MessageKey }[] = [
  { id: "new", title: "changes.newTitle" },
  { id: "changed", title: "changes.changedTitle" },
  { id: "resolved", title: "changes.resolvedTitle" },
];

function ShowOnMap({ orgId, nr, findingKey }: { orgId: string; nr: string; findingKey: string }) {
  const navigate = useNavigate();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<ApiError | null>(null);

  async function go() {
    setBusy(true);
    setError(null);
    try {
      const f = await unwrap(
        api.GET("/api/orgs/{org_id}/kommuner/{nummer}/findings/{key}", {
          params: { path: { org_id: orgId, nummer: nr, key: findingKey } },
        }),
      );
      const school = f.school_ids[0];
      if (school) navigate(routes.school(orgId, nr, school, f.key));
      else setError(new ApiError(404, t("finding.notInRun")));
    } catch (err) {
      setError(toApiError(err));
    } finally {
      setBusy(false);
    }
  }

  return (
    <>
      <button type="button" className="btn btn-link" onClick={() => void go()} disabled={busy}>
        {t("changes.showOnMap")}
      </button>
      {error && (
        <span className="field-error" role="alert">
          {error.userMessage}
        </span>
      )}
    </>
  );
}

export function ChangeDetailPage() {
  const { orgId, nr, navn } = useKommune();
  const { changeId = "" } = useParams();
  const detail = useQuery(`change:${orgId}:${nr}:${changeId}`, (signal) =>
    unwrap(
      api.GET("/api/orgs/{org_id}/kommuner/{nummer}/changes/{change_id}", {
        params: { path: { org_id: orgId, nummer: nr, change_id: changeId } },
        signal,
      }),
    ),
  );
  const c = detail.data;
  const date = c ? formatDateTime(c.created_at) : "";
  usePageTitle(c ? t("changes.detailTitle", { date }) : t("changes.title"));

  return (
    <div className="page">
      <p className="breadcrumbs">
        <Link to={routes.changes(orgId, nr)}>{t("changes.backToList")}</Link>
      </p>
      {detail.error && <ErrorMessage error={detail.error} onRetry={detail.reload} />}
      {!c && !detail.error && <Loading />}
      {c && (
        <>
          <header className="page-header">
            <p className="eyebrow">{navn}</p>
            <h1>{t("changes.detailTitle", { date })}</h1>
            <p className="muted">
              {t("changes.summary", {
                new: formatNumber(c.counts.new ?? 0),
                resolved: formatNumber(c.counts.resolved ?? 0),
                changed: formatNumber(c.counts.changed ?? 0),
              })}
            </p>
          </header>
          {GROUPS.map((g) => {
            const items = readItems(c.diff[g.id]);
            const headingId = `change-${g.id}`;
            return (
              <section key={g.id} className="section" aria-labelledby={headingId}>
                <h2 id={headingId}>
                  {t(g.title)} <span className="count-pill">{items.length}</span>
                </h2>
                {g.id === "resolved" && items.length > 0 && (
                  <p className="muted">{t("changes.resolvedNote")}</p>
                )}
                {items.length === 0 ? (
                  <p>{t("changes.none")}</p>
                ) : (
                  <ul className="change-list">
                    {items.map((item) => (
                      <li key={item.key} className="change-item">
                        <div className="change-main">
                          {g.id === "changed" && item.from && item.to ? (
                            <span className="change-severity">
                              <SeverityBadge severity={item.from} />
                              <span aria-hidden="true">→</span>
                              <SeverityBadge severity={item.to} />
                              <span className="visually-hidden">
                                {t("changes.fromTo", {
                                  from: severityLabel(item.from),
                                  to: severityLabel(item.to),
                                })}
                              </span>
                            </span>
                          ) : (
                            <SeverityBadge severity={item.severity} />
                          )}
                          <span className="change-title">{item.title}</span>
                          <span className="change-location">{item.location}</span>
                          {item.rank !== null && (
                            <span className="muted small">
                              {t("changes.rank", { rank: item.rank })}
                            </span>
                          )}
                        </div>
                        {g.id !== "resolved" && (
                          <div className="change-links">
                            <Link to={routes.tiltak(orgId, nr, { q: item.location })}>
                              {t("changes.showInList")}
                            </Link>
                            <ShowOnMap orgId={orgId} nr={nr} findingKey={item.key} />
                          </div>
                        )}
                      </li>
                    ))}
                  </ul>
                )}
              </section>
            );
          })}
        </>
      )}
    </div>
  );
}
